import React from 'react';
import { Drawer, Form, Input, Select, Button, message, Upload } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import './CreateTemplateDrawer.css';
import { templateService } from '../../../../utils/api';
import { TEMPLATE_CATEGORIES } from '../..';
import { useCompressionImageUpload } from '../../../../hooks/useCompressionImageUpload';
import CompressionConfirmModal from '../../../ImageCompressionModal/CompressionConfirmModal';

export default function CreateTemplateDrawer({ open, onClose, onCreate }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [fileList, setFileList] = React.useState([]);
  /** Cloudinary URL (or empty); sent as `thumbnail` on create. */
  const [thumbnailUrl, setThumbnailUrl] = React.useState('');
  const [thumbUploadBusy, setThumbUploadBusy] = React.useState(false);
  const { uploadImageFile, compressionModalProps } = useCompressionImageUpload({
    zIndex: 11000,
    uploadSuccessMessage: 'Image uploaded successfully',
  });
  const categories = TEMPLATE_CATEGORIES;

  React.useEffect(() => {
    if (!open) {
      form.resetFields();
      setThumbnailUrl('');
      setFileList([]);
    }
  }, [open, form]);

  const beforeUploadThumbnail = async (file) => {
    if (!file.type?.startsWith?.('image/')) {
      message.error('Please select an image file');
      return false;
    }
    setThumbUploadBusy(true);
    try {
      const url = await uploadImageFile(file);
      setThumbnailUrl(url);
      setFileList([{ uid: file.uid, name: file.name, status: 'done', url }]);
    } catch (error) {
      const cancelled = String(error?.message || '').toLowerCase().includes('cancel');
      if (!cancelled) {
        message.error(error?.message || 'Thumbnail upload failed');
      }
      setFileList([]);
      setThumbnailUrl('');
    } finally {
      setThumbUploadBusy(false);
    }
    return false;
  };

  const handleUploadChange = ({ fileList: newFileList }) => {
    if (newFileList.length === 0) {
      setThumbnailUrl('');
      setFileList([]);
    }
  };

  const tagRender = ({ label, closable, onClose }) => {
    const display = typeof label === 'string' && label.length > 10 ? label.slice(0, 10) + '…' : label;
    return (
      <span className="ctd-select-tag">
        {display}
        {closable && (
          <span className="ctd-select-tag-close" onClick={onClose}>×</span>
        )}
      </span>
    );
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const payload = {
        name: values.name.trim(),
        milepadData: '[]',
        isActive: values.isActive === 'true' || values.isActive === true,
        sort: 0,
        thumbnail: thumbnailUrl || 'string',
        docType: 'milegrid',
        category: values.categories || []
      };
      const res = await templateService.createTemplate(payload);
      const createdTemplate = res?.data?.data;
      const templateId = createdTemplate?._id;

      message.success('Template created successfully');
      onCreate?.();
      onClose();

      if (templateId) {
        window.open(`/template/${templateId}`, '_blank');
      }
    } catch (error) {
      if (error?.errorFields) return; // validation error, do nothing
      const msg = error?.response?.data?.message || 'Failed to create template';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const FooterBar = (
    <div className="ctd-bar">
      <Button
        type="primary"
        size="large"
        block
        className="ctd-create-btn"
        loading={loading || thumbUploadBusy}
        disabled={loading || thumbUploadBusy}
        onClick={handleSubmit}
      >
        Create Template
      </Button>
    </div>
  );

  return (
    <Drawer
      className="ctd-drawer"
      title={<span style={{ fontWeight: 600, color: '#526bb1' }}>Create Template</span>}
      visible={open}
      onClose={onClose}
      width={640}
      destroyOnClose={false}
      styles={{ body: { background: '#f5f6f7', padding: 16 } }}
    >
      <div className="ctd" style={{ background: '#fff', borderRadius: 8, padding: '20px' }}>
        <Form form={form} layout="vertical" requiredMark={true} initialValues={{ isActive: 'true' }}>
          <Form.Item
            label={<span className="ctd-label">Name</span>}
            name="name"
            rules={[{ required: true, message: 'Enter template name' }]}
          >
            <Input placeholder="Enter template name" />
          </Form.Item>

          <Form.Item
            label={<span className="ctd-label">Active</span>}
            name="isActive"
            rules={[{ required: true, message: 'Select status' }]}
          >
            <Select placeholder="Select status">
              <Select.Option value="true">Active</Select.Option>
              <Select.Option value="false">Inactive</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={<span className="ctd-label">Categories</span>}
            name="categories"
            rules={[{ required: true, message: 'Select categories' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select categories"
              showArrow
              className="ctd-categories-select"
              tagRender={tagRender}
              maxTagCount={5}
              maxTagPlaceholder={selected => `+${selected.length} more`}
            >
              {(categories || []).map(cat => (
                <Select.Option key={cat} value={cat}>
                  {cat}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label={<span className="ctd-label">Thumbnail</span>}>
            <Upload
              accept="image/*"
              listType="picture"
              fileList={fileList}
              beforeUpload={beforeUploadThumbnail}
              onChange={handleUploadChange}
              maxCount={1}
            >
              {fileList.length === 0 && (
                <div className="ctd-upload-placeholder">
                  <PlusOutlined style={{ marginRight: 6, fontSize: 11 }} />
                  {thumbUploadBusy ? 'Uploading…' : 'Upload thumbnail image'}
                </div>
              )}
            </Upload>
          </Form.Item>
        </Form>

        <div style={{ paddingTop: 8 }}>
          {FooterBar}
        </div>
      </div>
      <CompressionConfirmModal {...compressionModalProps} />
    </Drawer>
  );
}